import { FC } from "react";

import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  Button,
  ButtonText,
  Heading,
  HStack,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";

interface ConfirmDialogProps {
  action?: "negative" | "primary";
  body: string;
  confirmLabel: string;
  error?: string | null;
  isOpen: boolean;
  loading?: boolean;
  loadingLabel: string;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
  title: string;
}

const ConfirmDialog: FC<ConfirmDialogProps> = ({
  action = "negative",
  body,
  confirmLabel,
  error,
  isOpen,
  loading = false,
  loadingLabel,
  onCancel,
  onConfirm,
  title,
}) => {
  const { i18n } = useLocalization();

  return (
    <AlertDialog isOpen={isOpen} onClose={onCancel}>
      <AlertDialogBackdrop />
      <AlertDialogContent className="gap-6">
        <AlertDialogHeader>
          <Heading size="lg">{title}</Heading>
        </AlertDialogHeader>
        <AlertDialogBody>
          <Text>{body}</Text>
        </AlertDialogBody>
        <AlertDialogFooter>
          <VStack className="gap-2">
            <HStack className="gap-3 justify-end">
              <Button
                className="rounded-xl"
                isDisabled={loading}
                onPress={onCancel}
                variant="outline"
              >
                <ButtonText>{i18n.t("misc.cancel")}</ButtonText>
              </Button>
              <Button
                action={action}
                className="rounded-xl"
                isDisabled={loading}
                onPress={onConfirm}
              >
                <ButtonText>{loading ? loadingLabel : confirmLabel}</ButtonText>
              </Button>
            </HStack>
            {error && (
              <Text className="text-error-600 text-sm text-right">{error}</Text>
            )}
          </VStack>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmDialog;
